import { Injectable } from '@angular/core';

import { QPointDateFormat } from './DateFormat';
import { QPointLocalizationService } from './localization.service';
import { formatDate, splitDateFormat } from '@progress/kendo-angular-intl';
import { utcToZonedTime } from 'date-fns-tz';

@Injectable({
  providedIn: 'root'
})
export class QPointDateFormatService {
  public currentDateFormat: QPointDateFormat;

  constructor(private localizationService: QPointLocalizationService) {
    this.currentDateFormat = QPointDateFormat.ShortDateTime;
  }

  /** Method that formats date to given DateFormat  */
  public format(date: any, format?: QPointDateFormat, inTimezone?: string) {
    if (format != null) {
      return this.verifyDate(date, format, undefined, inTimezone);
    }

    return this.verifyDate(date, this.currentDateFormat, inTimezone);
  }

  /** Method that formats date to a custom format string  */
  public formatToCustomDateFormat(date: any, format: string, cultureForDateFormatting?: string, inTimezone?: string) {
    if (format != null) {
      return this.verifyDate(date, format, cultureForDateFormatting, inTimezone);
    }

    return this.verifyDate(date, this.currentDateFormat, cultureForDateFormatting, inTimezone);
  }

  /** Handles the date as string or Date object. If the format is invalid, then it returns 'N/A' */
  private verifyDate(date: any, dateFormat: string, cultureForDateFormatting?: string, inTimezone?: string): string {
    const localizedFormat = this.getLocalizedFormat(dateFormat, cultureForDateFormatting);
    if (typeof date === 'string') {
      if (date !== '') {
        return formatDate(this.localizationService.moment(date, inTimezone),
          localizedFormat, this.localizationService.currentLocale);
      }
    }
    if (Object.prototype.toString.call(date) === '[object Date]') {
      const dateInput = inTimezone
        ? utcToZonedTime(date, inTimezone) // JavaScript Dates are internally stored in UTC
        : date;
      return formatDate(dateInput, localizedFormat, this.localizationService.currentLocale);
    }
    console.warn('DateFormatService: Not able to format "' + date + '" into a valid date.');
    return 'N/A';
  }

  getLocalizedFormat(dateFormat: string, cultureForDateFormatting?: string) {
    const formats = dateFormat.split(' ');
    const localizedFormats = [];

    const culture = cultureForDateFormatting ?? this.localizationService.cultureForDateFormatting;

    formats.forEach(f => {
      const format = splitDateFormat(f, culture)?.map(part => part.pattern)?.join('');
      if (format) {
        localizedFormats.push(format);
      } else {
        localizedFormats.push(f);
      }
    });

    return localizedFormats.join(' ');
  }
}
